/* Shared, locale-aware cart state. Shopify remains authoritative for prices and shipping. */
if (!window.FitFamCartExperience && document.getElementById('FitFamCartConfig')) {
  const config = JSON.parse(document.getElementById('FitFamCartConfig').textContent);
  const root = config.root?.endsWith('/') ? config.root : `${config.root || ''}/`;
  const cents = value => {
    const match = String(value ?? '').match(/^(\d+)(?:\.(\d{1,2}))?$/);
    return match ? Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0')) : null;
  };
  const money = (value, currency = config.currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-NZ', { style: 'currency', currency }).format(value / 100);
  const isPickup = rate => /pick.?up|collect/i.test(rate.name || '') || rate.delivery_method_type === 'pick-up';
  const node = (tag, text, attrs = {}) => {
    const element = document.createElement(tag);
    if (text != null) element.textContent = text;
    for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, value);
    return element;
  };
  const thresholds = String(config.thresholds || 'NZ:NZD:100').split(/\r?\n/).map(line => line.trim().split(':'));
  const threshold = cents((thresholds.find(p => p.length === 3 && p[0] === 'NZ' && p[1] === 'NZD') || thresholds.find(p => p.length === 2 && p[0] === 'NZD') || []).at(-1));
  const excluded = new Set((config.excluded || []).map(String));
  const customers = new Set();
  const state = {
    config, money, cents, cart: null, address: null, rates: null, rateMessage: '', rateBusy: false, submittedAddress: false, pending: false, feedback: '', selections: new Map(),
    notify() {
      if (this.cart) document.querySelectorAll('[data-cart-item-count]').forEach(n => { n.textContent = this.cart.item_count; });
      customers.forEach(customer => customer.render());
    },
    mutationCount: 0, mutationVersion: 0, equipmentId: null,
    updateBusy() {
      const busy = this.mutationCount > 0;
      document.documentElement.toggleAttribute('data-ff-cart-busy', busy);
      for (const element of document.querySelectorAll('.ff-cart-draft :is(.cart__checkout-button,quantity-input input,quantity-input button,cart-remove-button button,.ff-topup button)')) {
        if (busy && !element.disabled) { element.dataset.ffCartDisabled = 'true'; element.disabled = true; }
        else if (!busy && element.dataset.ffCartDisabled) { delete element.dataset.ffCartDisabled; element.disabled = false; }
      }
      document.querySelectorAll('.ff-cart-draft .cart__dynamic-checkout-buttons').forEach(n => { n.inert = busy; });
      this.notify();
    },
    mutate(action, affectsShipping = true) {
      if (affectsShipping) this.invalidateRates();
      this.mutationCount++; this.mutationVersion++; this.updateBusy();
      const pending = (this.mutationTail || Promise.resolve()).catch(() => {}).then(action);
      this.mutationTail = pending.finally(() => {
        this.mutationCount--; this.updateBusy();
        if (!this.mutationCount) this.refresh();
      });
      return this.mutationTail;
    },
    amount() { return Number(this.cart?.[config.basis === 'items_subtotal_price' ? 'items_subtotal_price' : 'total_price'] || 0); },
    inMarket() { return (this.cart?.currency || config.currency) === 'NZD' && (this.address?.country ? this.address.country === 'New Zealand' : config.country === 'NZ'); },
    hasExcluded() { return this.cart?.items?.some(item => excluded.has(String(item.product_id)) || item.gift_card || !item.requires_shipping); },
    gap() { return Math.max(0, (threshold || 0) + (config.inclusive ? 0 : 1) - this.amount()); },
    qualified() { return !!this.cart?.requires_shipping && this.inMarket() && !this.hasExcluded() && threshold > 0 && !this.gap() && (config.verified || this.freeDeliveryEstimate()); },
    freeDeliveryEstimate() { return this.rates?.some(rate => !isPickup(rate) && cents(rate.price) === 0); },
    async refresh() {
      if (this.mutationCount) {this.dirty=true;return;}
      if (this.refreshing) { this.dirty = true; return this.refreshing; }
      this.refreshing = (async () => {
        do {
          this.dirty = false;
          try {
            const version = this.mutationVersion;
            const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
            if (!response.ok) throw new Error('Cart refresh failed.');
            const cart = await response.json();
            if (version !== this.mutationVersion || this.mutationCount) {this.dirty=true;return;}
            const signature = JSON.stringify([cart.currency, cart.total_price, cart.items_subtotal_price, cart.total_weight, cart.requires_shipping, cart.total_discount, cart.discount_codes, cart.cart_level_discount_applications, cart.attributes, cart.items?.map(item => [item.key, item.quantity, item.final_line_price])]);
            const changed = signature !== this.signature;
            this.cart = cart; this.signature = signature;
            if (changed) {
              this.invalidateRates();
              if (this.submittedAddress && cart.item_count && !this.qualified()) this.queueEstimate();
            }
            this.notify();
          } catch (_) {
            this.cart = null;
            this.feedback = 'Unable to refresh delivery progress. Check your cart and confirm shipping at checkout.';
            this.notify();
          }
        } while (this.dirty);
      })();
      try { await this.refreshing; } finally { this.refreshing = null; if(this.dirty && !this.mutationCount)this.refresh(); }
    },
    variants(product) {
      return product.variants.filter(v => v.available && !v.requiresPlan && v.requiresShipping !== false && v.min <= 1 && v.step > 0 && (1 - v.min) % v.step === 0 && (v.max == null || v.max >= 1));
    },
    recommendations() {
      if (!this.cart?.item_count || !config.recommendations) return [];
      const inCart = new Set(this.cart.items.map(item => String(item.product_id)));
      const seen = new Set();
      // Merchant-selected equipment, in saved order. Do not infer products or rank by price.
      return (config.products || []).filter(p => {
        if (seen.has(String(p.id))) return false;
        seen.add(String(p.id));
        return !inCart.has(String(p.id)) && !excluded.has(String(p.id)) && !p.giftCard && !p.requiresPlan && this.variants(p).length;
      });
    },
    async targets(trigger) {
      const drawer = document.querySelector('cart-drawer'), items = document.querySelector('cart-items'), notification = document.querySelector('cart-notification');
      const ids = new Set(['cart-icon-bubble']);
      for (const [name, element] of [['cart-drawer', drawer], ['cart-notification', notification]]) {
        if (!element) continue;
        await customElements.whenDefined(name);
        if (name === 'cart-notification') window.loadTemplateContent?.(element);
        element.init?.(); element.setActiveElement?.(trigger?.isConnected ? trigger : document.getElementById('cart-icon-bubble'));
        element.getSectionsToRender().forEach(section => ids.add(section.id));
      }
      items?.getSectionsToRender().forEach(section => ids.add(section.section));
      return { drawer, items, notification, ids: [...ids].slice(0, 5) };
    },
    renderCart(targets, result, preventOpen = false) {
      if (!result.sections || targets.ids.some(id => !result.sections[id])) throw new Error('The cart changed but could not be displayed.');
      targets.drawer?.renderContents(result, preventOpen);
      targets.items?.renderContents(result);
      if (!preventOpen && result.items?.[0]?.key) targets.notification?.renderContents({ ...result, key: result.items[0].key });
      const count = document.getElementById('cart-icon-bubble');
      const html = new DOMParser().parseFromString(result.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (count && html) count.innerHTML = html.innerHTML;
    },
    async add(product, variant, trigger) {
      if (this.pending || this.mutationCount || !this.variants(product).some(v => v.id === variant?.id)) return;
      this.pending = true; this.feedback = 'Adding to cart…'; this.notify();
      let targets;
      try {
        await this.mutate(async () => {
        targets = await this.targets(trigger);
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: targets.ids, sections_url: location.pathname }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'This item could not be added.');
        this.renderCart(targets, result);
        const fresh = await fetch(`${root}cart.js`, {cache:'no-store'});
        if (!fresh.ok) throw new Error('The item was added but the cart could not be confirmed.');
        this.cart = await fresh.json(); this.notify();
        this.feedback = 'Added to your cart.';
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fitfam-cart-tools', cartData: result, sectionsRendered: true });
        });
        await this.refresh();
      } catch (error) {
        // Never retry an ambiguous POST: it may already have added the item.
        try {
          if (targets) {
            const response = await fetch(`${location.pathname}?sections=${targets.ids.join(',')}`);
            if (!response.ok) throw new Error();
            this.renderCart(targets, { sections: await response.json() }, true);
          }
        } catch (_) { /* Checkout/cart links remain usable even if section reconciliation fails. */ }
        await this.refresh();
        this.feedback = `${error.message || 'Unable to confirm this addition.'} Check the cart before trying again.`;
      } finally { this.pending = false; this.notify(); }
    },
    invalidateRates() {
      this.rateVersion = (this.rateVersion || 0) + 1;
      this.rateController?.abort();
      this.rates = null; this.rateMessage = ''; this.rateBusy = false;
      clearTimeout(this.rateTimer);
    },
    queueEstimate() { clearTimeout(this.rateTimer); this.rateTimer = setTimeout(() => this.estimate(), 600); },
    async estimate() {
      this.invalidateRates();
      if (this.qualified()) { this.notify(); return; }
      if (!this.cart?.item_count || !this.cart.requires_shipping) { this.rateMessage = 'No delivery estimate is needed for this cart.'; this.notify(); return; }
      if ((this.cart.currency || config.currency) !== config.shopCurrency) { this.rateMessage = 'Shipping for this currency is confirmed at checkout.'; this.notify(); return; }
      if (!this.address?.country || !this.address.zip || this.addressValid === false) return;
      this.submittedAddress = true; this.rateBusy = true; this.rateMessage = 'Getting delivery estimates…'; this.notify();
      const version = this.rateVersion, controller = this.rateController = new AbortController();
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(this.address)) params.set(`shipping_address[${key}]`, value);
      const timeout = setTimeout(() => controller.abort(), 18000);
      try {
        const prepared = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`, { method: 'POST', signal: controller.signal });
        if (!prepared.ok) throw new Error();
        let result = null;
        for (let attempt = 0; attempt < 16; attempt++) {
          const response = await fetch(`${root}cart/async_shipping_rates.json?${params}`, { signal: controller.signal, cache: 'no-store' });
          if (!response.ok) throw new Error();
          result = await response.json();
          if (result !== null) break;
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        if (version !== this.rateVersion) return;
        if (!Array.isArray(result?.shipping_rates)) throw new Error();
        this.rates = result.shipping_rates.filter(rate => cents(rate.price) != null && (!rate.currency || rate.currency === this.cart.currency));
        this.rateMessage = this.rates.length ? '' : 'No delivery rates were returned for this address. Check the address or continue to checkout for available options.';
      } catch (_) {
        if (version === this.rateVersion) this.rateMessage = 'Shipping could not be estimated. Please try again or confirm shipping at checkout.';
      } finally {
        clearTimeout(timeout);
        if (version === this.rateVersion) { this.rateBusy = false; this.notify(); }
      }
    }
  };
  window.FitFamCartExperience = state;

  if (!customElements.get('fitfam-cart-tools')) customElements.define('fitfam-cart-tools', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      customers.add(this);
      const form = this.querySelector('[data-estimate-form]');
      if (form) {
        const country = form.elements.country;
        country.value = state.address?.country || (config.country === 'NZ' ? 'New Zealand' : country.options[0]?.value);
        this.provinces();
        if (state.address) for (const [key, value] of Object.entries(state.address)) if (form.elements[key]) form.elements[key].value = value;
        form.addEventListener('submit', event => { event.preventDefault(); if (form.reportValidity()) { state.address = Object.fromEntries(new FormData(form)); state.addressValid = true; state.estimate(); } }, { signal: this.abort.signal });
        form.addEventListener('input', () => {
          state.address = Object.fromEntries(new FormData(form)); state.addressValid = form.checkValidity(); state.invalidateRates(); state.notify();
          if (state.submittedAddress && form.checkValidity()) state.queueEstimate();
        }, { signal: this.abort.signal });
        country.addEventListener('change', () => { this.provinces(); state.address = Object.fromEntries(new FormData(form)); state.addressValid = form.checkValidity(); state.invalidateRates(); state.notify(); if (state.submittedAddress && form.checkValidity()) state.queueEstimate(); }, { signal: this.abort.signal });
      }
      for (const direction of ['prev', 'next']) this.querySelector(`[data-equipment-${direction}]`)?.addEventListener('click', () => {
        const products = state.recommendations();
        const current = Math.max(0, products.findIndex(p => String(p.id) === state.equipmentId));
        const index = Math.max(0, Math.min(products.length - 1, current + (direction === 'next' ? 1 : -1)));
        state.equipmentId = String(products[index]?.id || '');
        state.feedback = '';
        state.notify();
        (this.querySelector(`[data-equipment-${direction}]:not(:disabled)`) || this.querySelector('.ff-equipment-nav button:not(:disabled)'))?.focus({preventScroll:true});
      }, {signal:this.abort.signal});
      this.render();
      if (!state.cart) state.refresh();
    }
    disconnectedCallback() { customers.delete(this); this.abort?.abort(); this.abort = null; }
    provinces() {
      const form = this.querySelector('form');
      const options = JSON.parse(form.elements.country.selectedOptions[0]?.dataset.provinces || '[]');
      form.elements.province.replaceChildren(...options.map(([value, label]) => node('option', label, { value })));
      if (options.length) form.elements.province.prepend(node('option', 'Choose a region', { value: '' }));
      form.elements.province.value = '';
      form.querySelector('[data-province-label]').hidden = !options.length;
      form.elements.province.required = !!options.length;
    }
    render() {
      if (!this.isConnected) return;
      if (this.dataset.mode === 'summary') {
        this.hidden = !state.cart?.item_count;
        const text = !state.cart?.requires_shipping ? 'No delivery required.' : state.qualified() ? 'Shipping: FREE · Includes rural NZ delivery.' : 'Shipping calculated at checkout.';
        this.querySelector('[data-shipping-summary]').textContent = text;
        return;
      }
      if (this.dataset.mode === 'estimate') {
        this.hidden = !config.estimator || !state.cart?.item_count;
        const result = this.querySelector('[data-estimate-result]');
        if (!result) return;
        const form = this.querySelector('form');
        const qualified = state.qualified();
        this.querySelector('details').hidden = qualified;
        this.querySelector('[data-free-shipping]').hidden = !qualified;
        if (qualified) return;
        const addressKey = JSON.stringify(state.address);
        if (state.address && addressKey !== this.addressKey && !form.contains(document.activeElement)) {
          form.elements.country.value = state.address.country;
          this.provinces();
          for (const [key, value] of Object.entries(state.address)) if (form.elements[key]) form.elements[key].value = value;
          this.addressKey = addressKey;
        }
        form.querySelector('button').disabled = state.rateBusy;
        const drawer = this.closest('cart-drawer');
        result.setAttribute('aria-live', drawer ? (drawer.classList.contains('active') ? 'polite' : 'off') : (document.querySelector('cart-drawer.active') ? 'off' : 'polite'));
        const estimateSignature = JSON.stringify([state.rateMessage, state.rates, state.cart?.total_price, state.cart?.currency, state.address]);
        if (estimateSignature === this.estimateSignature) return;
        this.estimateSignature = estimateSignature;
        result.replaceChildren();
        if (state.rateMessage) result.append(node('p', state.rateMessage));
        const delivery = (state.rates || []).filter(rate => !isPickup(rate)).sort((a,b) => cents(a.price) - cents(b.price))[0];
        const pickup = (state.rates || []).find(isPickup);
        if (delivery) result.append(node('p', `Shipping estimate: ${cents(delivery.price) === 0 ? 'FREE' : money(cents(delivery.price), state.cart.currency)}. Confirmed at checkout.`));
        else if (pickup) result.append(node('p', 'Only pickup was returned. Delivery is unavailable for this address; check the address or checkout options.'));
        return;
      }
      const progress = this.querySelector('[data-shipping-progress]');
      const showProgress = !!state.cart?.item_count && config.progress && state.inMarket() && threshold > 0;
      if (progress) progress.hidden = !showProgress;
      if (showProgress && progress) {
        let message;
        if (state.hasExcluded()) message = 'Shipping eligibility is confirmed at checkout for this cart.';
        else if (state.qualified()) message = 'You’ve unlocked FREE SHIPPING!';
        else if (!state.gap()) message = 'Free-shipping threshold reached. Confirm eligibility at checkout.';
        else message = `You’re ${money(state.gap(), 'NZD')} away from FREE SHIPPING.`;
        const messageNode = this.querySelector('[data-progress-message]');
        const drawer = this.closest('cart-drawer');
        messageNode.setAttribute('role', 'status');
        messageNode.setAttribute('aria-live', drawer ? (drawer.classList.contains('active') ? 'polite' : 'off') : (document.querySelector('cart-drawer.active') ? 'off' : 'polite'));
        if (messageNode.textContent !== message) messageNode.textContent = message;
        this.querySelector('progress').value = state.qualified() ? 100 : Math.min(99, state.amount() * 100 / threshold);
        this.querySelector('progress').hidden = !!state.hasExcluded();
        this.querySelector('[data-progress-note]').textContent = `NZ orders ${config.inclusive ? 'of' : 'over'} ${money(threshold, 'NZD')}${config.inclusive ? ' or more' : ''} qualify, including rural delivery. Calculated ${config.basis === 'items_subtotal_price' ? 'after line discounts' : 'after discounts'}. Other destinations and product restrictions are confirmed at checkout.`;
      }
      const recommendations = this.dataset.mode === 'recommendations' ? state.recommendations() : [];
      const index = Math.max(0, recommendations.findIndex(p => String(p.id) === state.equipmentId));
      const current = recommendations[index];
      if (current) state.equipmentId = String(current.id);
      const navigation = this.querySelector('[data-equipment-nav]');
      if (navigation) {
        navigation.hidden = recommendations.length < 2;
        this.querySelector('[data-equipment-position]').textContent = `${index + 1} / ${recommendations.length}`;
        this.querySelector('[data-equipment-prev]').disabled = index === 0;
        this.querySelector('[data-equipment-next]').disabled = index === recommendations.length - 1;
      }
      const topups = this.querySelector('[data-topups]');
      if (topups) topups.hidden = !recommendations.length;
      const list = this.querySelector('[data-topup-list]');
      const signature = JSON.stringify([current?.id, state.selections.get(String(current?.id)), state.pending, state.mutationCount > 0]);
      if (list && signature !== this.signature) {
        this.signature = signature;
        list.replaceChildren(...(current ? [this.card(current)] : []));
      }
      const feedback = this.querySelector('[data-cart-feedback]');
      // Only the visible surface announces updates; unchanged text is not reinserted.
      const drawer = this.closest('cart-drawer');
      feedback?.setAttribute('aria-live', drawer ? (drawer.classList.contains('active') ? 'polite' : 'off') : (document.querySelector('cart-drawer.active') ? 'off' : 'polite'));
      if (feedback && feedback.textContent !== state.feedback) feedback.textContent = state.feedback;
      this.hidden = this.dataset.mode === 'recommendations' ? (!recommendations.length && !state.feedback) : !showProgress;
    }
    card(product) {
      const card = node('article', null, { class: 'ff-topup', 'data-topup-id': String(product.id) });
      const info = node('div', null, { class: 'ff-topup__info' });
      const variants = state.variants(product);
      const selection = state.selections.get(String(product.id));
      const selected = product.variants.length === 1 ? variants[0] : variants.find(v => String(v.id) === selection);
      const image = node('img', null, { width: '64', height: '72', alt: '', loading: 'lazy' });
      if (selected?.image || product.image) { image.src = selected?.image || product.image; card.append(image); }
      else card.append(node('span', 'No photo', {class:'ff-equipment-no-image', 'aria-hidden':'true'}));
      const link = node('a', product.title, { href: product.url }); info.append(link);
      info.append(node('div', selected ? money(selected.price) : `From ${money(Math.min(...variants.map(v => v.price)))}`));
      if (product.variants.length > 1) {
        const select = node('select', null, { 'aria-label': `Choose an option for ${product.title}` });
        select.append(node('option', 'Choose an option', { value: '' }));
        for (const v of product.variants) { const option = node('option', `${v.title} — ${money(v.price)}${!v.available ? ' — Sold out' : !variants.includes(v) ? ' — Unavailable' : ''}`, { value: String(v.id) }); option.disabled = !variants.includes(v); select.append(option); }
        select.value = selected ? String(selected.id) : '';
        select.disabled = state.pending || state.mutationCount > 0;
        select.addEventListener('change', () => {
          state.selections.set(String(product.id), select.value); state.notify();
          [...this.querySelectorAll('[data-topup-id]')].find(item => item.dataset.topupId === String(product.id))?.querySelector('select')?.focus({ preventScroll: true });
        });
        info.append(select);
      }
      const button = node('button', state.pending ? 'Adding…' : 'Add', { type: 'button', 'aria-label': `Add ${product.title} to cart` });
      button.disabled = !selected || state.pending || state.mutationCount > 0;
      button.addEventListener('click', () => state.add(product, selected, button));
      card.append(info, button);
      return card;
    }
  });
  const listen = () => {
    if (state.unsubscribe || typeof subscribe !== 'function') return;
    state.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => state.refresh());
    if (PUB_SUB_EVENTS.cartOpen) subscribe(PUB_SUB_EVENTS.cartOpen, () => { state.notify(); state.refresh(); });
    if (PUB_SUB_EVENTS.cartClose) subscribe(PUB_SUB_EVENTS.cartClose, () => state.notify());
  };
  document.addEventListener('submit', event => {
    if (state.mutationCount && ['cart','CartDrawer-Form'].includes(event.target.id)) {
      event.preventDefault();
      const error = document.querySelector('cart-drawer.active #CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (error) error.textContent = 'Your cart is updating. Please wait before checking out.';
    }
  }, true);
  // The theme's instant-mousedown shortcut dispatches an uncancellable click.
  // Let this link use its normal click so pending cart/note saves can finish first.
  window.addEventListener('mousedown', event => {
    if (event.target.closest('[data-cart-navigation]')) event.stopPropagation();
  }, true);
  document.addEventListener('click', async event => {
    const link = event.target.closest('[data-cart-navigation]');
    if (!link || (!state.mutationCount && !state.pending)) return;
    event.preventDefault();
    if (state.navigating) return;
    state.navigating = true;
    const href = link.href;
    link.setAttribute('aria-busy', 'true');
    try {
      // Includes debounced notes and any mutations queued while the first save finishes.
      while (state.mutationCount || state.pending) {
        if (state.mutationTail) await state.mutationTail;
        if (state.pending) await new Promise(resolve => setTimeout(resolve, 50));
      }
      if (document.querySelector('cart-drawer.active #CartDrawer-CartErrors')?.textContent.trim()) return;
      location.assign(href);
    } catch (_) { /* The existing cart error remains visible; preserve the drawer and unsaved note. */ }
    finally { state.navigating = false; link.removeAttribute('aria-busy'); }
  }, true);
  listen(); window.addEventListener('load', listen, { once: true });
  window.addEventListener('pageshow', () => { if (customers.size) state.refresh(); });
  document.addEventListener('cart:updated', () => state.refresh());
}
